import { useEffect, useRef, useState } from "react";
import { Printer, X } from "lucide-react";
import { api, idr } from "../../lib/api";
import { formatTanggal } from "../../lib/tanggal";
import { cetak, resolveDim } from "../../components/faktur/FakturPrint";
import { nilaiMdr } from "../../lib/claimMdr";

/**
 * Cetak nota untuk satu baris Data Claim (brief 22 Sep butir 28).
 *
 * Isinya PERSIS daftar butir 28, tidak lebih:
 *   a. Tanggal Pembelian   b. Nama Konsumen   c. Tipe Produk   d. IMEI
 *   e. Serial Number       f. Harga Jual (**untuk GDN**)
 *   g. Jumlah = nominal kredit    h. Total = nominal kredit
 *   i. Hormat kami dst            j. Garansi 1 tahun
 *
 * JUDULNYA "NOTA PENJUALAN", BUKAN "NOTA KREDIT"
 * ----------------------------------------------
 * Brief menyebut "print nota untuk tiap data credit, dengan format nota
 * penjualan". Di repo ini "Credit Note" sudah jadi nama modul Finance sendiri
 * (`finance_credit_note`, nilai yang ditahan supplier) — memberi dokumen ini
 * judul "Nota Kredit" akan membuat dua dokumen berbeda bernama sama di satu
 * aplikasi. Kalau Bos memang mau judul lain, yang berubah hanya baris judul.
 *
 * KENAPA KOMPONEN SENDIRI, BUKAN FakturPrint
 * ------------------------------------------
 * Alasan yang sama dengan `MemoPrint.jsx`: FakturPrint dibangun untuk dokumen
 * ber-ITEM dari entitas `sale`/`po`/`transfer`, sementara ini satu baris Data
 * Claim. Yang DIPAKAI ULANG justru bagian yang penting: `cetak()` — ia
 * menyalin `.print-area` ke `#print-root` sebagai anak langsung <body>,
 * perbaikan untuk keluhan "faktur keluar 3-4 rangkap". `window.print()` mentah
 * di sini akan melahirkan bug rangkap itu kembali khusus untuk nota MDR.
 *
 * Angkanya dibaca lewat `nilaiMdr` — SAMA dengan yang dipakai tabel dan
 * dengan `ekspor.buat_xlsx`. Nota yang angkanya beda dari berkas yang dikirim
 * ke vendor adalah dua dokumen yang saling membantah.
 */
export default function ClaimMdrNotaPrint({ baris, tab, labelTab, namaLeasing, onTutup }) {
  const ref = useRef(null);
  const [template, setTemplate] = useState(null);

  // Kop & footer mengikuti template faktur PENJUALAN supaya tidak ada kop
  // kedua yang harus diurus terpisah (pola `FinancePiutang.jsx`). Gagal di
  // sini TIDAK boleh membatalkan cetaknya — notanya tetap keluar dengan kop
  // bawaan.
  useEffect(() => {
    let batal = false;
    api.get("/faktur/templates", { params: { jenis: "penjualan" } })
      .then((r) => { if (!batal) setTemplate((r.data?.items || [])[0] || null); })
      .catch(() => { /* kop bawaan */ });
    return () => { batal = true; };
  }, []);

  const t = template || {};
  const kop = t.kop || {};
  const dim = resolveDim(t.ukuran_kertas || "A5", t.orientasi || "");

  // Butir 28f: Harga Jual HANYA untuk GDN.
  const pakaiHargaJual = tab === "gdn";
  const hargaJual = nilaiMdr(baris, "harga_jual");
  // Butir 28g & 28h: Jumlah DAN Total keduanya = Nominal Kredit. Bukan
  // dihitung dari Harga Jual — untuk GDN keduanya memang angka yang berbeda,
  // dan yang ditagih/dibiayai adalah nominal kreditnya.
  const nominal = nilaiMdr(baris, "nominal");

  const baris28 = [
    ["Tanggal Pembelian", formatTanggal(nilaiMdr(baris, "tanggal_pembelian")) || "-"],
    ["Nama Konsumen", nilaiMdr(baris, "nama_konsumen") || "-"],
    ["Tipe Produk", nilaiMdr(baris, "tipe_produk") || "-"],
    ["IMEI", nilaiMdr(baris, "imei") || "-"],
    ["Serial Number", nilaiMdr(baris, "sn") || "-"],
    ["Leasing / CC", namaLeasing?.[nilaiMdr(baris, "leasing")] || nilaiMdr(baris, "leasing") || "-"],
    ["Tenor", nilaiMdr(baris, "tenor") || "-"],
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 overflow-y-auto"
      data-testid="mdr-nota-modal">
      <div className="my-8 w-full max-w-3xl">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => cetak(ref.current)} data-testid="mdr-nota-cetak"
            className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center gap-2">
            <Printer className="w-4 h-4" /> Cetak
          </button>
          <button onClick={onTutup} data-testid="mdr-nota-tutup"
            className="h-10 px-3 rounded-lg bg-white border border-slate-200 text-slate-600 text-sm font-medium inline-flex items-center gap-1.5">
            <X className="w-4 h-4" /> Tutup
          </button>
        </div>

        <div ref={ref} className="print-area bg-white text-slate-900 mx-auto shadow-sm border border-slate-200"
          style={{ width: dim?.width ? `${dim.width}mm` : "148mm",
                   minHeight: dim?.height ? `${dim.height}mm` : undefined,
                   padding: "12mm 10mm" }}
          data-testid="mdr-nota-print-area">
          <div className="flex items-start gap-4 border-b-2 border-slate-900 pb-3">
            {kop.tampil_logo !== false && kop.logo_url && (
              <img src={kop.logo_url} alt="" style={{ maxHeight: "48px" }} />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold leading-tight">
                {kop.judul || "CV ZOA INTERSTELLAR GROUP"}
              </p>
              {kop.alamat && <p className="text-[11px] leading-snug">{kop.alamat}</p>}
              {kop.telepon && <p className="text-[11px] leading-snug">{kop.telepon}</p>}
            </div>
          </div>

          <div className="text-center my-4">
            <p className="text-sm font-bold tracking-widest">NOTA PENJUALAN</p>
            {labelTab && (
              <p className="text-[11px] mt-0.5" data-testid="mdr-nota-tab">{labelTab}</p>
            )}
          </div>

          <table className="text-xs mb-4">
            <tbody>
              {baris28.map(([label, nilai]) => (
                <tr key={label}>
                  <td className="pr-3 py-0.5 align-top font-semibold whitespace-nowrap">{label}</td>
                  <td className="pr-2 align-top">:</td>
                  <td className="align-top">{nilai}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <table className="w-full text-xs border-collapse" data-testid="mdr-nota-rincian">
            <thead>
              <tr className="border-y border-slate-900">
                <th className="text-left py-1 font-semibold">Keterangan</th>
                {pakaiHargaJual && (
                  <th className="text-right py-1 font-semibold whitespace-nowrap"
                    data-testid="mdr-nota-kolom-harga-jual">Harga Jual</th>
                )}
                <th className="text-right py-1 font-semibold whitespace-nowrap">Jumlah</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-200">
                <td className="py-1.5 align-top">
                  {nilaiMdr(baris, "tipe_produk") || "-"}
                  <div className="text-[10px] text-slate-500">
                    IMEI {nilaiMdr(baris, "imei") || "-"} · SN {nilaiMdr(baris, "sn") || "-"}
                  </div>
                </td>
                {pakaiHargaJual && (
                  <td className="py-1.5 text-right align-top whitespace-nowrap"
                    data-testid="mdr-nota-harga-jual">
                    {hargaJual == null ? "-" : idr(hargaJual)}
                  </td>
                )}
                <td className="py-1.5 text-right align-top whitespace-nowrap font-mono"
                  data-testid="mdr-nota-jumlah">
                  {nominal == null ? "-" : idr(nominal)}
                </td>
              </tr>
              <tr>
                <td className="py-1.5 text-right font-semibold" colSpan={pakaiHargaJual ? 2 : 1}>
                  Total
                </td>
                <td className="py-1.5 text-right font-bold font-mono whitespace-nowrap"
                  data-testid="mdr-nota-total">
                  {nominal == null ? "-" : idr(nominal)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Butir 28j — apa adanya, bukan dihitung dari tanggal apa pun. */}
          <p className="mt-4 text-[11px]" data-testid="mdr-nota-garansi">Garansi 1 tahun.</p>

          {t.footer_teks && <p className="mt-3 text-[10px] leading-snug">{t.footer_teks}</p>}

          {/* Butir 28i */}
          <div className="mt-8 flex justify-end text-xs">
            <div className="text-center" style={{ minWidth: "45mm" }}>
              <p>Hormat kami,</p>
              <div style={{ height: "16mm" }} />
              <p className="border-t border-slate-900 pt-1">
                {kop.judul || "CV ZOA INTERSTELLAR GROUP"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
